import React from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import GymnasiumLanding from '../components/gymnasium/GymnasiumLanding';
import Arena from '../components/gymnasium/Arena';
import GauntletLibrary from '../components/gymnasium/GauntletLibrary';
import GauntletDetail from '../components/gymnasium/GauntletDetail';
import GauntletRunView from '../components/gymnasium/GauntletRunView';
import ComparativeRadar from '../components/gymnasium/ComparativeRadar';
import Leaderboard from '../components/gymnasium/Leaderboard';
import { FiHome, FiZap, FiTarget, FiBarChart2, FiAward } from 'react-icons/fi';

interface NavItem {
  key: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  gradient: string;
  glowColor: string;
  stat?: string;
}

const Gymnasium: React.FC = () => {
  const { darkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ gauntletId?: string; runId?: string }>();

  const path = location.pathname.replace(/^\/gymnasium\/?/, '');

  const getActiveKey = (): string => {
    if (!path || path === '') return 'home';
    if (path === 'arena') return 'arena';
    if (path.startsWith('gauntlets') || path.startsWith('runs')) return 'gauntlets';
    if (path === 'radar') return 'radar';
    if (path === 'rankings') return 'rankings';
    return 'home';
  };

  const activeKey = getActiveKey();

  const navItems: NavItem[] = [
    { key: 'home', label: 'Hub', path: '/gymnasium', icon: <FiHome className="w-3.5 h-3.5" />, gradient: 'from-amber-600 to-orange-600', glowColor: 'rgba(245,158,11,0.4)' },
    { key: 'arena', label: 'Arena', path: '/gymnasium/arena', icon: <FiZap className="w-3.5 h-3.5" />, gradient: 'from-amber-500 to-red-500', glowColor: 'rgba(239,68,68,0.3)' },
    { key: 'gauntlets', label: 'Gauntlets', path: '/gymnasium/gauntlets', icon: <FiTarget className="w-3.5 h-3.5" />, gradient: 'from-violet-600 to-purple-600', glowColor: 'rgba(139,92,246,0.4)' },
    { key: 'radar', label: 'Radar', path: '/gymnasium/radar', icon: <FiBarChart2 className="w-3.5 h-3.5" />, gradient: 'from-blue-500 to-cyan-500', glowColor: 'rgba(59,130,246,0.3)' },
    { key: 'rankings', label: 'Rankings', path: '/gymnasium/rankings', icon: <FiAward className="w-3.5 h-3.5" />, gradient: 'from-amber-500 to-amber-600', glowColor: 'rgba(245,158,11,0.4)' },
  ];

  const getView = () => {
    if (!path || path === '') return <GymnasiumLanding />;
    if (path === 'arena') return <Arena />;
    if (path === 'gauntlets' && !params.gauntletId) return <GauntletLibrary />;
    if (path.startsWith('gauntlets/') && params.gauntletId) return <GauntletDetail gauntletId={params.gauntletId} />;
    if (path.startsWith('runs/') && params.runId) return <GauntletRunView runId={params.runId} />;
    if (path === 'radar') return <ComparativeRadar />;
    if (path === 'rankings') return <Leaderboard />;
    return <GymnasiumLanding />;
  };

  return (
    <div className={`w-full ${darkMode ? 'text-white' : 'text-gray-800'}`}>
      {/* ── Header — full treatment ── */}
      <div className={`rounded-lg shadow-lg border-l-4 px-4 py-3 mb-3 ${
        darkMode ? 'bg-gray-800 border-amber-600' : 'bg-white border-amber-500 shadow-sm'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${darkMode ? 'bg-amber-900/30' : 'bg-amber-50'}`}>
              <FiAward className={`h-5 w-5 ${darkMode ? 'text-amber-300' : 'text-amber-700'}`} />
            </div>
            <div>
              <h1 className={`text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r ${
                darkMode ? 'from-amber-400 to-orange-400' : 'from-amber-600 to-orange-600'
              }`}>Gymnasium</h1>
              <p className={`text-xs italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>The proving ground for your personas</p>
            </div>
          </div>
        </div>
      </div>
      <div className="h-px mb-3 bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />

      {/* ── Persistent Nav Bar ── */}
      <div className="flex gap-3 mb-4">
        {navItems.map(item => {
          const isActive = item.key === activeKey;
          return (
            <button
              key={item.key}
              onClick={() => navigate(item.path)}
              className={`relative px-5 py-3 rounded-xl font-semibold transition-all duration-300 overflow-hidden flex items-center gap-2.5 ${
                isActive
                  ? `bg-gradient-to-br ${item.gradient} text-white scale-[1.03] ring-2 ring-white/25`
                  : darkMode
                    ? 'bg-gray-800/70 text-gray-400 hover:bg-gray-700 hover:text-gray-100 hover:scale-[1.01] hover:ring-1 hover:ring-white/10'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-800 hover:scale-[1.01]'
              }`}
              style={isActive ? {
                boxShadow: `0 6px 25px ${item.glowColor}, 0 0 80px ${item.glowColor.replace(/[\d.]+\)$/, '0.15)')}`,
              } : undefined}
            >
              {/* Hatching overlay on active */}
              {isActive && (
                <div className="absolute inset-0 opacity-[0.06]" style={{
                  backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(255,255,255,0.6) 8px, rgba(255,255,255,0.6) 16px)',
                }} />
              )}
              <span className="relative z-10 text-base">{item.icon}</span>
              <span className="relative z-10 text-sm">{item.label}</span>
            </button>
          );
        })}
      </div>

      {/* ── View Content ── */}
      {getView()}
    </div>
  );
};

export default Gymnasium;
